import { getDefinition } from "../catalog";
import { effectiveTerminals, isStudTerminal } from "../effective";
import { SCHEMA_VERSION } from "../schema/version";
import type {
  Cable,
  ComponentDefinition,
  ComponentNode,
  System,
  Terminal,
} from "../types";
import type { AiSystemPolarity } from "../../../functions/_ai/system-schema";
import {
  AI_SYSTEM_EDIT_MAX_CONTEXT_CABLES,
  AI_SYSTEM_EDIT_MAX_CONTEXT_NODES,
} from "../../../functions/_ai/system-edit-schema";

/**
 * Never add a computed number here — wire size, fuse rating, V-drop: a model
 * that can read one echoes it back as the system's own answer.
 *
 * Edge-safe — the Pages Function bundles this file, so nothing it imports may
 * reach `import.meta.env`, `posthog-js` or `@supabase/*`.
 */

export type SystemDigestNode = {
  /** Unique within a digest. */
  ref: string;
  catalogId: string;
  label: string | null;
  /** Only on a busbar — synthesized studs are not on the catalog entry. */
  studCount?: number;
  /** Inline def for a `"custom"` part; without it the endpoint rejects every edit. */
  custom?: ComponentDefinition;
};

export type SystemDigestCable = {
  from: string;
  fromTerminalId: string;
  to: string;
  toTerminalId: string;
  polarity: AiSystemPolarity;
  lengthM: number;
};

export type SystemDigest = {
  name: string;
  nodes: SystemDigestNode[];
  cables: SystemDigestCable[];
};

export type BuiltSystemDigest = {
  digest: SystemDigest;
  /** The half that never leaves the browser. */
  refToNodeId: Map<string, string>;
};

export function systemIsEditableByAi(system: System): boolean {
  const parts = componentNodes(system).length;
  return (
    parts > 0 &&
    parts <= AI_SYSTEM_EDIT_MAX_CONTEXT_NODES &&
    system.cables.length <= AI_SYSTEM_EDIT_MAX_CONTEXT_CABLES
  );
}

/** Zones are skipped — no electrical role, and the delta schema cannot name one. */
export function buildSystemDigest(system: System): BuiltSystemDigest {
  const refToNodeId = new Map<string, string>();
  const refByNodeId = new Map<string, string>();
  const used = new Set<string>();

  const nodes: SystemDigestNode[] = [];
  for (const node of componentNodes(system)) {
    const def = getDefinition(node.definitionId, node.customDefinition);
    if (!def) continue;
    const ref = uniqueRef(refBase(node, def), used);
    refToNodeId.set(ref, node.id);
    refByNodeId.set(node.id, ref);
    const entry: SystemDigestNode = {
      ref,
      catalogId: node.definitionId,
      label: node.label ?? null,
    };
    if (node.studCount !== undefined) entry.studCount = node.studCount;
    if (node.definitionId === "custom" && node.customDefinition) {
      entry.custom = node.customDefinition;
    }
    nodes.push(entry);
  }

  const cables: SystemDigestCable[] = [];
  for (const cable of system.cables) {
    const from = refByNodeId.get(cable.fromNodeId);
    const to = refByNodeId.get(cable.toNodeId);
    if (!from || !to) continue;
    cables.push({
      from,
      fromTerminalId: cable.fromTerminalId,
      to,
      toTerminalId: cable.toTerminalId,
      polarity: cablePolarity(system, cable),
      lengthM: cable.lengthM,
    });
  }

  return { digest: { name: system.name, nodes, cables }, refToNodeId };
}

/** Rebuild a system from a digest for the endpoint's gate. Node ids **are** the refs,
 *  so the returned ref → node id map is the identity. */
export function digestToSystem(
  digest: SystemDigest,
): { system: System; refToNodeId: Map<string, string> } | null {
  const refToNodeId = new Map<string, string>();
  const nodes: ComponentNode[] = [];
  for (const entry of digest.nodes) {
    const def = getDefinition(entry.catalogId, entry.custom);
    if (!def) return null;
    const node: ComponentNode = {
      id: entry.ref,
      kind: "component",
      definitionId: entry.catalogId,
      position: { x: 0, y: 0 },
    };
    if (entry.custom) node.customDefinition = entry.custom;
    if (entry.label) node.label = entry.label;
    if (entry.studCount !== undefined) node.studCount = entry.studCount;
    nodes.push(node);
    refToNodeId.set(entry.ref, entry.ref);
  }

  const known = new Set(refToNodeId.keys());
  const cables: Cable[] = [];
  digest.cables.forEach((c, i) => {
    if (!known.has(c.from) || !known.has(c.to)) return;
    cables.push({
      id: `x${i + 1}`,
      fromNodeId: c.from,
      fromTerminalId: c.fromTerminalId,
      toNodeId: c.to,
      toTerminalId: c.toTerminalId,
      lengthM: c.lengthM,
    });
  });

  return {
    system: {
      name: digest.name,
      nodes,
      cables,
      zoneDistances: {},
      version: SCHEMA_VERSION,
    },
    refToNodeId,
  };
}

/** Validate a digest off the wire. Shape and bounds only. */
export function parseSystemDigest(raw: unknown): SystemDigest | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  if (!Array.isArray(o.nodes) || !Array.isArray(o.cables)) return null;
  if (!o.nodes.length || o.nodes.length > AI_SYSTEM_EDIT_MAX_CONTEXT_NODES) return null;
  if (o.cables.length > AI_SYSTEM_EDIT_MAX_CONTEXT_CABLES) return null;

  const nodes: SystemDigestNode[] = [];
  const refs = new Set<string>();
  for (const n of o.nodes) {
    if (!n || typeof n !== "object") return null;
    const entry = n as Record<string, unknown>;
    const ref = typeof entry.ref === "string" ? entry.ref.trim() : "";
    const catalogId =
      typeof entry.catalogId === "string" ? entry.catalogId.trim() : "";
    if (!ref || !catalogId || refs.has(ref)) return null;
    refs.add(ref);
    const node: SystemDigestNode = {
      ref,
      catalogId,
      label: typeof entry.label === "string" && entry.label.trim() ? entry.label.trim() : null,
    };
    if (typeof entry.studCount === "number" && Number.isFinite(entry.studCount)) {
      node.studCount = Math.max(1, Math.round(entry.studCount));
    }
    // Cast unchecked: it reaches the model only as a name, and `digestToSystem`
    // fails closed on it.
    if (entry.custom && typeof entry.custom === "object") {
      node.custom = entry.custom as ComponentDefinition;
    }
    nodes.push(node);
  }

  const cables: SystemDigestCable[] = [];
  for (const c of o.cables) {
    if (!c || typeof c !== "object") return null;
    const cable = c as Record<string, unknown>;
    const from = typeof cable.from === "string" ? cable.from : "";
    const to = typeof cable.to === "string" ? cable.to : "";
    const fromTerminalId =
      typeof cable.fromTerminalId === "string" ? cable.fromTerminalId : "";
    const toTerminalId =
      typeof cable.toTerminalId === "string" ? cable.toTerminalId : "";
    if (!refs.has(from) || !refs.has(to) || !fromTerminalId || !toTerminalId) {
      return null;
    }
    cables.push({
      from,
      fromTerminalId,
      to,
      toTerminalId,
      polarity: cable.polarity === "negative" ? "negative" : "positive",
      lengthM:
        typeof cable.lengthM === "number" && Number.isFinite(cable.lengthM)
          ? cable.lengthM
          : 1,
    });
  }

  return {
    name: typeof o.name === "string" ? o.name.trim() : "",
    nodes,
    cables,
  };
}

/** English catalog names deliberately — this is a prompt, not a UI, so no i18n here. */
export function renderSystemDigest(digest: SystemDigest): string {
  const landings = new Map<string, Map<string, number>>();
  for (const cable of digest.cables) {
    bump(landings, cable.from, cable.fromTerminalId);
    bump(landings, cable.to, cable.toTerminalId);
  }

  const lines: string[] = [];
  lines.push(
    `The system on the canvas${digest.name ? ` ("${digest.name}")` : ""} — one line per part, "ref  id  name":`,
  );
  for (const node of digest.nodes) {
    const def = getDefinition(node.catalogId, node.custom);
    if (!def) continue;
    const name = node.label ? `${node.label} (${def.name})` : def.name;
    const free = freeSlotNote(def, node, landings.get(node.ref));
    lines.push(`  ${node.ref}  ${node.catalogId}  ${name}${free ? `  [${free}]` : ""}`);
  }

  lines.push("");
  lines.push(
    digest.cables.length
      ? "Cables already wired — these stay unless you remove them:"
      : "No cables are wired yet.",
  );
  for (const cable of digest.cables) {
    lines.push(
      `  ${cable.from} → ${cable.to}  ${cable.polarity}  ${cable.lengthM} m`,
    );
  }
  return lines.join("\n");
}

function componentNodes(system: System): ComponentNode[] {
  return system.nodes.filter((n): n is ComponentNode => n.kind === "component");
}

function cablePolarity(system: System, cable: Cable): AiSystemPolarity {
  const ends: Array<[string, string]> = [
    [cable.fromNodeId, cable.fromTerminalId],
    [cable.toNodeId, cable.toTerminalId],
  ];
  for (const [nodeId, terminalId] of ends) {
    const node = system.nodes.find((n) => n.id === nodeId);
    if (!node || node.kind !== "component") continue;
    const def = getDefinition(node.definitionId, node.customDefinition);
    if (!def) continue;
    const terminal = effectiveTerminals(def, node).find((t) => t.id === terminalId);
    if (!terminal) continue;
    if (terminal.polarity === "positive") return "positive";
    if (terminal.polarity === "negative" || terminal.polarity === "ground") {
      return "negative";
    }
  }
  return "positive";
}

function freeSlotNote(
  def: ComponentDefinition,
  node: SystemDigestNode,
  landings: Map<string, number> | undefined,
): string | null {
  const terminals = effectiveTerminals(def, { studCount: node.studCount });
  const taken = (t: Terminal) => (landings?.get(t.id) ?? 0) > 0;

  if (def.isFuseBlock) {
    const slots = terminals.filter((t) => t.direction === "output");
    if (slots.length >= 2) {
      const free = slots.filter((t) => !taken(t)).length;
      return `${free} of ${slots.length} fused circuits free`;
    }
  }
  if (def.renderVariant === "busbar") {
    const studs = terminals.filter((t) => isStudTerminal(def, t));
    if (studs.length) {
      const free = studs.filter((t) => !taken(t)).length;
      return free > 0
        ? `${free} of ${studs.length} studs free`
        : `${studs.length} studs, all in use (more can be added)`;
    }
  }
  return null;
}

/** Full `name`, never `shortName`: "+ Bus" and "− Bus" both slug down to `bus`. */
function refBase(node: ComponentNode, def: ComponentDefinition): string {
  return slug(node.label ?? def.name ?? node.definitionId) || "part";
}

function slug(raw: string): string {
  return (
    raw
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 28)
      // Again after the slice: the cut can land mid-word.
      .replace(/-+$/, "")
  );
}

function uniqueRef(base: string, used: Set<string>): string {
  let ref = base;
  let n = 2;
  while (used.has(ref)) ref = `${base}-${n++}`;
  used.add(ref);
  return ref;
}

function bump(map: Map<string, Map<string, number>>, ref: string, terminalId: string): void {
  const inner = map.get(ref) ?? new Map<string, number>();
  inner.set(terminalId, (inner.get(terminalId) ?? 0) + 1);
  map.set(ref, inner);
}
